// Light/dark appearance: follows the system unless the user picked one.
import { useSyncExternalStore } from 'react';
import { readJson, writeJson } from './storage';

export type ThemeChoice = 'system' | 'light' | 'dark';

const KEY = 'ph:theme';
const listeners = new Set<() => void>();
const media = window.matchMedia('(prefers-color-scheme: dark)');
let choice: ThemeChoice = readJson<ThemeChoice>(KEY) ?? 'system';

function resolved(): 'light' | 'dark' {
  return choice === 'system' ? (media.matches ? 'dark' : 'light') : choice;
}

function apply() {
  const theme = resolved();
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = theme === 'dark' ? '#0a1330' : '#e8edf8';
  for (const listener of listeners) listener();
}

media.addEventListener('change', apply);
apply();

export function setTheme(next: ThemeChoice): void {
  choice = next;
  writeJson(KEY, next);
  apply();
}

export function useTheme(): ThemeChoice {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => choice,
  );
}
